import { type BackgroundJob, createHomepageBackgroundQueue } from "@/lib/homepage-background-queue";
import { isImpromptuClientEnabled, resolveImpromptuLanguageCode } from "@/lib/impromptu/config";
import { preloadLanguageJourney } from "@/lib/languageJourneyClient";
import { loadImpromptu, loadSettings } from "@/lib/secondary-feature-loaders";
import { useCallback, useEffect, useRef } from "react";

export function useHomepageBackgroundLoading({
  ready,
  busy,
  languageCode,
}: {
  ready: boolean;
  busy: boolean;
  languageCode: string | null;
}) {
  const queue = useRef<ReturnType<typeof createHomepageBackgroundQueue> | null>(null);
  const priority = useRef(false);
  const eligibility = useRef({ ready, busy });
  eligibility.current = { ready, busy };
  useEffect(() => {
    const instance = createHomepageBackgroundQueue(window);
    queue.current = instance;
    return () => {
      instance.dispose();
      queue.current = null;
    };
  }, []);
  useEffect(() => {
    const jobs: BackgroundJob[] = [loadSettings, () => preloadLanguageJourney(languageCode)];
    if (isImpromptuClientEnabled() && resolveImpromptuLanguageCode(languageCode))
      jobs.push(loadImpromptu);
    queue.current?.replace(jobs);
  }, [languageCode]);
  useEffect(() => {
    queue.current?.pause(!ready || busy || priority.current);
  }, [ready, busy]);
  const prioritizeSpeaking = useCallback(() => {
    priority.current = true;
    queue.current?.pause(true);
  }, []);
  const releaseSpeakingPriority = useCallback(() => {
    priority.current = false;
    queue.current?.pause(!eligibility.current.ready || eligibility.current.busy);
  }, []);
  return { prioritizeSpeaking, releaseSpeakingPriority };
}
